import React from "react";

import { every, map, trim } from "lodash";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import Button from "components/Button";

import Alert from "components/Alert";

import { emailValidator, testUsername } from "utils/fieldValidators";
import { rolesToEnglish } from "statics/roleToEnglish";
import { ScrollToFieldError } from "utils/form";

import * as yup from "yup";

const roles = ["AGENT", "ADMIN"];

interface Administrator {
  user: {
    email: string;
    username: string;
  };
}

interface AdministratorFormProps {
  isInviting?: boolean;
  onSubmit?: (values: FormValues) => void;
  error?: string;
  fixedRole?: string;
  administrators?: Administrator[];
  onCancel?: () => void;
}

interface State {
  showForm: boolean;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  // written via setState in _onSubmit / warning handlers; read in render but
  // never set in the initial state, so it is optional.
  showEmailDomainWarning?: boolean;
}

interface FormValues {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  administrators?: Administrator[];
}

function testExistingUserEmail(
  this: yup.TestContext,
  email: string | undefined
) {
  return every(
    (this.parent as { administrators?: Administrator[] }).administrators,
    ({ user: admin }) => admin.email !== email
  );
}

function testExistingUsername(
  this: yup.TestContext,
  username: string | undefined
) {
  return every(
    (this.parent as { administrators?: Administrator[] }).administrators,
    ({ user: admin }) => admin.username !== username
  );
}

const schema = yup.object({
  email: emailValidator("You must enter a valid email address.")
    .label("Administrator Email")
    .test(
      "administrators",
      "User has already been invited.",
      testExistingUserEmail
    )
    .required(),
  username: yup
    .string()
    .label("Administrator Username")
    .required()
    .min(2)
    .max(25)
    .test("username", "User has already been invited.", testExistingUsername)
    // testUsername's `this` type ({ createError }) is disjoint from yup's
    // TestFunction signature; boundary-cast so its runtime behavior is kept.
    .test(
      "username",
      "Invalid username.",
      testUsername as unknown as yup.TestFunction<string | undefined>
    ),
  role: yup.string().label("Role").required(),

  firstName: yup.string().label("First Name").required(),
  lastName: yup.string().label("Last Name").required(),
});

export default class AdministratorForm extends React.PureComponent<
  AdministratorFormProps,
  State
> {
  constructor(props: AdministratorFormProps) {
    super(props);

    this.state = {
      showForm: false,
      email: "",
      username: "",
      firstName: "",
      lastName: "",
      role: "AGENT",
    };
  }

  _onSubmit = (values: FormValues, skipValidation?: boolean) => {
    const emailDomainMatches = this._validateEmailDomain(values.email);

    if (!emailDomainMatches && !skipValidation) {
      return this.setState({
        showEmailDomainWarning: true,
        email: values.email,
        username: values.username,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
      });
    }

    this.props.onSubmit!({
      ...values,
      firstName: trim(values.firstName),
      lastName: trim(values.lastName),
      username: trim(values.username),
    });
  };

  _validateEmailDomain = (email: string) => {
    const preexistingDomains = map(this.props.administrators, (admin) => {
      return admin.user.email.split("@")[1];
    });

    const emailDomain = email.split("@")[1];

    return preexistingDomains.includes(emailDomain);
  };

  _getEmailDomainWarning = () => {
    return (
      <div className="domain-warning">
        <p>
          Warning: The domain in the new administrators email (
          {this.state.email}) does not match existing administrator email
          domains.
        </p>
        <p>Is this correct?</p>
        <div className="btn-row">
          <Button
            btnLabel="Go Back"
            color="cancel"
            name="cancel"
            type="button"
            onClick={() => {
              this.setState({ showEmailDomainWarning: false });
            }}
          />
          <Button
            btnLabel="Continue"
            color="primary"
            name="action"
            type="button"
            onClick={() => {
              this.setState({ showEmailDomainWarning: false });
              this._onSubmit(
                {
                  email: this.state.email,
                  username: this.state.username,
                  firstName: this.state.firstName,
                  lastName: this.state.lastName,
                  role: this.state.role,
                },
                true
              );
            }}
          />
        </div>
      </div>
    );
  };

  render() {
    const role = this.props.fixedRole || this.state.role;

    if (this.state.showEmailDomainWarning) {
      return this._getEmailDomainWarning();
    }

    return (
      <div className="employee-invite-form">
        <Formik<FormValues>
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={(values) => this._onSubmit(values)}
          enableReinitialize={true}
          initialValues={{
            ...this.state,
            role,
            administrators: this.props.administrators,
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            handleBlur,
          }: FormikProps<FormValues>) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formBasicUserEmail">
                  <Form.Label>Administrator Email</Form.Label>
                  <Form.Control
                    name="email"
                    placeholder="Administrator Email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={touched.email && !!errors.email}
                    isValid={touched.email && !errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formBasicUsername">
                  <Form.Label>Administrator Username</Form.Label>
                  <Form.Control
                    name="username"
                    placeholder="Administrator Username"
                    value={values.username}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={touched.username && !!errors.username}
                    isValid={touched.username && !errors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formAdminRole">
                  <Form.Label>Administrator role</Form.Label>
                  <Form.Select
                    // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                    sm={4}
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!!this.props.fixedRole}
                    isInvalid={touched.role && !!errors.role}
                    isValid={touched.role && !errors.role}
                  >
                    <option value="" disabled>
                      Administrator Role
                    </option>
                    {roles.map((role) => (
                      <option value={role} key={role}>
                        {rolesToEnglish[role as keyof typeof rolesToEnglish]}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.role}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formBasicFirstName">
                  <Form.Label>Administrator First Name</Form.Label>
                  <Form.Control
                    name="firstName"
                    value={values.firstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={touched.firstName && !!errors.firstName}
                    isValid={touched.firstName && !errors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={6} controlId="formBasicLastName">
                  <Form.Label>Administrator Last Name</Form.Label>
                  <Form.Control
                    name="lastName"
                    value={values.lastName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={touched.lastName && !!errors.lastName}
                    isValid={touched.lastName && !errors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}

              <div className="submit-row">
                <Button
                  btnLabel="Cancel"
                  color="cancel"
                  name="cancel"
                  type="button"
                  onClick={() => {
                    this.props.onCancel!();
                  }}
                />

                <Button
                  btnLabel="Invite Administrator"
                  name="submit"
                  withArrow={true}
                  loading={this.props.isInviting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}
